<template>
  <div class="header">
    <div class="header__title">
      <a-icon
        type="arrow-left"
        v-if="showReturnButton"
        @click="onReturnButtonClick()"
        class="header__title__return"
      />
      {{ pageTitle }}

      <template v-if="route.name === 'sync-drafts'">
        <a
          :href="syncUrl"
          target="_blank"
          class="header__url"
        >
          {{ syncUrl }}
        </a>
      </template>
    </div>

    <div
      class="header__meta"
      @mousedown.prevent
    >
      <a-dropdown
        :trigger="['click']"
        placement="bottomRight"
        :overlayStyle="{ height: 300 }"
      >
        <div class="header__user">
          <div class="header__user-description">
            <div
              class="header__user-name"
              v-if="userName"
            >
              {{ userName }}
            </div>
            <div
              class="header__user-phone-number"
              v-if="phoneNumber"
            >
              {{ formattedPhoneNumber }}
            </div>
          </div>
          <a-avatar
            shape="square"
            :icon="hasProfile ? 'user' : 'loading'"
            size="large"
          />
        </div>

        <a-menu
          style="min-width: 200px"
          slot="overlay"
        >
          <a-menu-item>
            <a @click.prevent="logOut">
              <a-icon type="logout" />
              {{ $t("user.logout") }}
            </a>
          </a-menu-item>
          <a-menu-item class="header__overlay__transparent-item">
            <LanguageSwitcher monochrome />
          </a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>
  </div>
</template>

<script setup lang="ts">
import { VueMaskFilter } from "v-mask"
import { computed, getCurrentInstance } from "vue"
import { isNavigationFailure } from "vue-router"
import { useRoute, useRouter } from "vue-router/composables"

import i18n from "@/i18n"
import { LOGOUT } from "@/store/modules/auth/actionTypes"

import LanguageSwitcher from "../Common/LanguageSwitcher/LanguageSwitcher.vue"

const proxy = getCurrentInstance()?.proxy as any

const route = useRoute()
const router = useRouter()

const showReturnButton = computed(() => {
  return Boolean(route?.meta?.returnButtonName)
})
const profile = computed(() => proxy.$store.state.user?.profile || {})
const user = computed(() => proxy.$store.state.user?.user || {})
const hasProfile = computed(() => Object.keys(profile.value).length > 0)
const pageTitle = computed(() => {
  const metaTitle = route?.meta?.title

  if (metaTitle && metaTitle === "reviews.title") {
    return i18n.t(metaTitle, [profile.value.name]) || i18n.t("app.title")
  } else if (metaTitle) {
    return i18n.t(metaTitle) || i18n.t("app.title")
  }
})
const phoneNumber = computed(() => user.value.mobile_phone)
const userName = computed(() => user.value.username || proxy.fullName)

const logOut = () => {
  proxy.$store.dispatch("auth/" + LOGOUT)
}
const onReturnButtonClick = async () => {
  try {
    await router.push({ name: route.meta.returnButtonName })
  } catch (error) {
    if (!isNavigationFailure(error)) throw error
  }
}

const formattedPhoneNumber = computed(() => {
  if (!phoneNumber.value) return ""

  return VueMaskFilter(phoneNumber.value, "+## (###) ### ## ##")
})
const syncUrl = computed(() => {
  const url = route.query.url
  return Array.isArray(url) ? url[0] : url
})
</script>

<style lang="scss" scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: $header-height;
  max-height: $header-height;
  border-bottom: 1px solid $header-border-color;
  padding: 0 24px;

  &__title {
    font-size: $heding-font-size;
    color: $font-color-black-accent;

    &__return {
      margin-right: 16px;
    }
  }

  &__url {
    margin-left: 12px;
    color: $brand-color-6;
    text-decoration: none;
    font-size: 14px;
  }

  &__meta {
    display: flex;
    align-items: center;
    line-height: 1.6;
  }

  &__user {
    display: flex;
    align-items: center;
    cursor: pointer;

    &-description {
      margin-right: 8px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    &-name {
      font-weight: $font-weight-bold;
      color: $font-color-black-accent;
    }

    &-phone-number {
      color: $gray-color-7;
    }
  }

  &__deactivated {
    color: $red-color-5;
    font-weight: $font-weight-bold;
    margin-right: 16px;
  }

  &__overlay {
    &__transparent-item:hover {
      background: transparent;
    }
  }
}
</style>
